/**
 * Dictionary of all action types with their display text
 */
export const actionTypesDictionary = {
    action_show: 'Animate Entrance',
    action_hide: 'Animate Exit',
    action_play: 'Play',
    action_pause: 'Pause',
    action_toggle_play_pause: 'Toggle Play Pause',
    action_stop: 'Stop',
    action_seek: 'Seek',
    action_end: 'End',
    action_set_materials_variant: 'Set materials variant',
    action_animate: 'Animate',
    action_spin: 'Spin',
    action_orbit: 'Orbit',
    action_play_particles: 'Play Particles',
    action_change_space: 'Change Space',
    action_open_url: 'Open Link',
    action_show_instructions: 'Show Instructions',
    action_open_home: 'Open Home',
    action_raise_event: 'Raise Event',
    action_set_experience_origin: 'Reset User Origin',
    action_history_back: 'Back to previous space',
    action_start_following: 'Attach to Screen',
    action_stop_following: 'Detach from Screen',
    action_transform_to: 'Transform to',
    action_transform_by: 'Transform by',
    action_move_in_direction: 'Move in Direction',
    action_force_end_sequence: 'Force End Sequence',
    trigger_image_anchor_found: 'Image Found',
};

/**
 * Dictionary of actions that require timing controls
 */
export const timingControlActions = [
    'action_show',
    'action_hide',
    'action_play',
    'action_animate',
    'action_spin',
    'action_orbit',
    'action_transform_to',
    'action_transform_by',
    'action_move_in_direction',
];

/**
 * Dictionary of actions that support waiting until finished
 */
export const waitUntilFinishedActions = [
    'action_play',
    'action_animate',
    'action_open_url',
    'action_show',
    'action_hide',
    'action_transform_to',
    'action_transform_by',
    'action_move_in_direction',
    'action_spin',
    'action_orbit',];

/**
 * Dictionary of actions that don't require targets
 */
export const nonTargetingActions = [
    'action_change_space',
    'action_open_home',
    'action_force_end_sequence',
    'action_set_experience_origin',
    'action_open_url',
    'action_show_instructions',
    'action_send_amqp_message',
    'action_send_mqtt_message',
    'action_raise_event',
    'action_history_back',
    'trigger_image_anchor_found',
];

/**
 * Dictionary of action start types
 */
export const actionStartDictionary = {
    with_previous: 'with previous',
    after_previous: 'after previous',
};

/**
 * Gets the display text for an action type
 * 
 * @param {string} actionType - The action type value
 * @returns {string} The display text for the action type
 */
export const getActionTypeText = (actionType) => {
    return actionTypesDictionary[actionType] || actionType;
};

/**
 * Checks if an action requires timing controls
 * 
 * @param {string} actionType - The action type value
 * @returns {boolean} Whether the action type requires timing controls
 */
export const requiresTimingControls = (actionType) => {
    return timingControlActions.includes(actionType);
};

/**
 * Checks if an action supports waiting until finished
 * 
 * @param {string} actionType - The action type value
 * @returns {boolean} Whether the action type supports waiting until finished
 */
export const supportsWaitUntilFinished = (actionType) => {
    return waitUntilFinishedActions.includes(actionType);
};

/**
 * Checks if an action requires target objects
 * 
 * @param {string} actionType - The action type value
 * @returns {boolean} Whether the action type requires target objects
 */
export const requiresTargetObjects = (actionType) => {
    return !nonTargetingActions.includes(actionType);
};

/**
 * Gets all available action types based on user role
 * 
 * @param {string} userRole - The user's role ('admin', 'staff', or 'user')
 * @returns {Object} Dictionary of available action types
 */
export const getAvailableActionTypes = (userRole = 'user') => {
    // Start with all action types
    const available = { ...actionTypesDictionary };

    // Filter based on user role
    if (userRole !== 'admin' && userRole !== 'staff') {
        // Remove admin/staff-only action types for regular users
        delete available.action_transform_by;
        delete available.action_transform_to;
        delete available.action_set_materials_variant;
        delete available.action_raise_event;
    }

    return available;
};
